import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import SitemarkIcon from "../components/landing/SitemarkIcon";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";

const EFFECTIVE_DATE = "April 1, 2026";
const COMPANY_NAME = "Savac Plus";
const CONTACT_EMAIL = "legal@savacplus.com";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 1.5 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
      {items.map((item, i) => (
        <Box component="li" key={i} sx={{ mb: 0.75 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <>
      <CssBaseline enableColorScheme />
      <Box sx={{ position: "absolute", top: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } }}>
        <ColorModeIconDropdown />
      </Box>
      <Container maxWidth="md" sx={{ py: { xs: 6, sm: 10 } }}>
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: "center" }}>
          <Box sx={{ cursor: "pointer", display: "inline-block", mb: 3 }} onClick={() => navigate("/")}>
            <SitemarkIcon />
          </Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Effective date: {EFFECTIVE_DATE}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Para>
          These Terms of Service ("Terms") govern your use of {COMPANY_NAME} ("we", "us", "our") and
          the services accessible through our platform. By creating an account or using the service, you
          agree to these Terms. If you do not agree, do not use the service.
        </Para>

        {/* 1 */}
        <Section title="1. The Service">
          <Para>
            {COMPANY_NAME} provides a software platform that allows businesses ("Customers") to upload
            audio recordings of sales calls, receive AI-generated transcriptions and analysis, score
            individual sales representatives against configurable criteria, and track team performance
            over time.
          </Para>
          <Para>
            We do not guarantee any specific business outcomes, improvements in sales conversion rates,
            or accuracy of AI-generated analysis beyond commercially reasonable standards. The service is
            provided as a tool to assist human decision-making, not to replace it.
          </Para>
        </Section>

        {/* 2 */}
        <Section title="2. Call Recording Consent — Your Responsibility">
          <Para>
            <strong>
              This is the most important section of these Terms. Please read it carefully.
            </strong>
          </Para>
          <Para>
            Laws governing the recording of telephone calls and other conversations vary significantly
            by country, state, and jurisdiction. In some jurisdictions, all parties to a conversation
            must consent to being recorded ("all-party" or "two-party" consent laws). These include, but
            are not limited to, California, Florida, Illinois, Pennsylvania, and several other US states,
            as well as many countries under applicable data protection legislation.
          </Para>
          <Para>
            By uploading any audio recording to {COMPANY_NAME}, you represent and warrant that:
          </Para>
          <BulletList items={[
            "You have obtained all legally required consents from every participant in each recorded conversation before uploading it to our platform.",
            "The recording, storage, and analysis of the uploaded content is lawful in the jurisdiction(s) where the conversation took place and where the participants are located.",
            "You will not upload recordings of conversations in which the participants have not been informed that the call may be recorded, where such notice is required by law.",
            "You are solely responsible for compliance with all applicable call recording laws, including but not limited to the Electronic Communications Privacy Act (ECPA), state wiretapping statutes, GDPR, and any other applicable law.",
          ]} />
          <Para>
            {COMPANY_NAME} bears no responsibility for your compliance with recording consent laws. You
            agree to indemnify and hold {COMPANY_NAME} harmless from any claims, damages, fines, or
            legal costs arising from your failure to obtain proper consent before recording or uploading
            any call.
          </Para>
        </Section>

        {/* 3 */}
        <Section title="3. Acceptable Use">
          <Para>You agree not to use the service to:</Para>
          <BulletList items={[
            "Upload recordings you do not have legal rights to record or share.",
            "Upload content that is unlawful, harassing, defamatory, or that violates any third party's rights.",
            "Reverse-engineer, decompile, or attempt to extract the source code or underlying AI models of the platform.",
            "Resell, sublicense, or otherwise commercially exploit access to the platform without our written permission.",
            "Attempt to circumvent usage limits, account restrictions, or security measures.",
            "Use the platform to train competing AI systems or models.",
          ]} />
        </Section>

        {/* 4 */}
        <Section title="4. Accounts and Access">
          <Para>
            You must provide accurate information when creating an account. You are responsible for
            maintaining the confidentiality of your login credentials and for all activity that occurs
            under your account. Notify us immediately at{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link> if you believe your account
            has been compromised.
          </Para>
          <Para>
            Each account is for a single company or organization. You may add individual team members
            ("users") within the limits of your plan. You are responsible for ensuring that all users
            under your account comply with these Terms.
          </Para>
        </Section>

        {/* 5 */}
        <Section title="5. Payment, Billing, and Cancellation">
          <Para>
            Subscription fees are billed monthly in advance. If you choose the free trial, your chosen
            plan's fee will be charged automatically at the end of the trial period unless you cancel
            before it ends.
          </Para>
          <Para>
            All fees are non-refundable except where required by applicable law. We reserve the right
            to change pricing with at least 30 days' written notice. Continued use of the service after
            a price change constitutes acceptance of the new pricing.
          </Para>
          <Para>
            You may cancel your subscription at any time from your account settings. Cancellation takes
            effect at the end of the current billing period. We reserve the right to suspend or terminate
            accounts with unpaid balances after reasonable notice.
          </Para>
        </Section>

        {/* 6 */}
        <Section title="6. Data and Privacy">
          <Para>
            Your use of the service is also governed by our{" "}
            <Link href="/privacy" target="_blank">Privacy Policy</Link>, which is incorporated into
            these Terms by reference. By using the service, you consent to our collection and use of
            data as described in the Privacy Policy.
          </Para>
          <Para>
            You retain ownership of all audio recordings, transcripts, and data you upload. By uploading
            content, you grant {COMPANY_NAME} a limited license to process, store, and analyze that
            content solely for the purpose of providing the service to you.
          </Para>
          <Para>
            We will not sell your data or use your call recordings to train AI models without your
            explicit written consent.
          </Para>
        </Section>

        {/* 7 */}
        <Section title="7. Intellectual Property">
          <Para>
            {COMPANY_NAME} retains all rights to the platform, including its software, AI models,
            scoring algorithms, user interface, and branding. Nothing in these Terms transfers any
            ownership of our intellectual property to you.
          </Para>
          <Para>
            Any feedback, suggestions, or ideas you provide about the service may be used by us without
            obligation or compensation to you.
          </Para>
        </Section>

        {/* 8 */}
        <Section title="8. Disclaimers and Limitation of Liability">
          <Para>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, OR NON-INFRINGEMENT.
          </Para>
          <Para>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()}'S TOTAL LIABILITY
            TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT
            EXCEED THE TOTAL FEES PAID BY YOU IN THE THREE (3) MONTHS PRECEDING THE CLAIM.
          </Para>
          <Para>
            IN NO EVENT SHALL {COMPANY_NAME.toUpperCase()} BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS
            OPPORTUNITY, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </Para>
        </Section>

        {/* 9 */}
        <Section title="9. Termination">
          <Para>
            Either party may terminate these Terms at any time. We may suspend or terminate your access
            immediately and without notice if we reasonably believe you have violated these Terms,
            particularly provisions related to recording consent and acceptable use.
          </Para>
          <Para>
            Upon termination, your access to the service will cease. You may request export of your
            data within 30 days of termination, after which we may delete it in accordance with our
            data retention policy.
          </Para>
        </Section>

        {/* 10 */}
        <Section title="10. Changes to These Terms">
          <Para>
            We may update these Terms from time to time. We will notify you of material changes by
            email or by displaying a notice in the application at least 14 days before the changes take
            effect. Your continued use of the service after the effective date constitutes acceptance of
            the updated Terms.
          </Para>
        </Section>

        {/* 11 */}
        <Section title="11. Contact">
          <Para>
            If you have questions about these Terms, contact us at:{" "}
            <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
          </Para>
        </Section>

        <Divider sx={{ mt: 2, mb: 4 }} />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Link href="/privacy" variant="body2" color="text.secondary">
            Privacy Policy
          </Link>
          <Typography variant="body2" color="text.disabled">·</Typography>
          <Link
            component="button"
            variant="body2"
            color="text.secondary"
            onClick={() => navigate("/")}
          >
            Back to Home
          </Link>
        </Box>
      </Container>
    </>
  );
}
